'use client';

import { buttonVariants } from '@magicschool/spellbook/components/Button';
import { Icon } from '@magicschool/spellbook/components/Icon';
import { Select } from '@magicschool/spellbook/components/Select';
import { textVariants } from '@magicschool/spellbook/components/Text';
import { ToggleButton } from '@magicschool/spellbook/components/ToggleButton';
import type { SpellbookNameOverrideProps } from '@magicschool/spellbook/lib/slots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { useEffect, useRef } from 'react';
import type { DayPickerProps, PropsSingle } from 'react-day-picker';
import { type DayButton, DayPicker, getDefaultClassNames } from 'react-day-picker';

export type CalendarProps = Omit<DayPickerProps & PropsSingle, 'mode' | 'captionLayout' | 'navLayout' | 'locale'> &
  SpellbookNameOverrideProps & {
    /** The language locale to use for formatting dates. Set to 'en-US' by default. See [MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat#locales_and_options) for more information on locales. */
    locale?: string;
  };

function CalendarDayButton({ className, day, modifiers, ...props }: Omit<React.ComponentProps<typeof DayButton>, 'color'>) {
  const dayPickerClassNames = getDefaultClassNames();
  const ref = useRef<HTMLButtonElement>(null);
  /**
   * only supporting single selection for now...
   * if you want to support range selection, you can use the `modifiers` prop
   * and check for `modifiers.range_start`, `modifiers.range_end`, and `modifiers.range_middle`
   * if you want to support multiple selection, you can use the `modifiers.selected`
   *
   * ```tsx
   *  data-selected-single={modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle}
   *  data-range-start={modifiers.range_start}
   *  data-range-end={modifiers.range_end}
   *  data-range-middle={modifiers.range_middle}
   * ```
   */
  const isSelected = Boolean(modifiers.selected);

  useEffect(() => {
    if (modifiers.focused && ref.current) {
      ref.current.focus();
    }
  }, [modifiers.focused]);

  return (
    <ToggleButton
      ref={ref}
      data-day={day.date.toLocaleDateString()}
      data-today={modifiers.today}
      data-outside={modifiers.outside}
      pressed={isSelected}
      className={cn(
        dayPickerClassNames.day,
        'aspect-square',
        'p-0',
        'shrink-0',
        'min-w-0',
        'relative',
        'focus-visible:z-1',
        'data-[today=true]:[&_span]:font-bold',
        'data-[state=off]:data-[outside=true]:[&[aria-disabled="false"]_span]:text-default-text-soft/50',
        className,
      )}
      {...props}
    />
  );
}

/**
 * Controlled single-date selection calendar with dropdown month/year navigation and locale support. Built on [react-day-picker](https://react-day-picker.js.org/).
 *
 * ### When to use
 * - Picking a single date in a form (e.g., birth date, due date, event date)
 * - When users need a visual calendar to browse and select a date
 * - When the selectable range needs to be constrained with `startMonth`, `endMonth`, or `disabled`
 *
 * ### When NOT to use
 * - For date range or multi-date selection: this component only supports single date mode
 * - For date-and-time selection: use a dedicated datetime picker
 * - For a simple text-based date field without a calendar UI: use `DateInput` instead
 *
 * ### API
 * - `locale`: Language locale string for formatting dates and weekday names. Defaults to `'en-US'`.
 * - `selected`: The currently selected `Date`. Manage externally — this is a controlled component.
 * - `onSelect`: Callback fired when a date is selected. Receives the selected `Date`.
 * - `required`: When `true`, a date must be selected.
 * - `defaultMonth`: The month displayed on initial render. Defaults to the current month.
 * - `startMonth` / `endMonth`: Constrain month/year dropdown navigation to a date range.
 * - `disabled`: Disable specific dates or ranges. Accepts a matcher (e.g., `{ after: new Date() }` to disable future dates).
 * - `showOutsideDays`: When `true`, renders days from adjacent months in empty cells.
 *
 * ### Visual states
 * - **Selected**: Day button renders as pressed (`data-state="on"`) with a highlighted background
 * - **Today**: Current date is bold (`data-today="true"`)
 * - **Outside**: Days from adjacent months render with reduced opacity (`data-outside="true"`)
 * - **Disabled**: Non-selectable dates are visually muted and set `aria-disabled="true"`
 * - **Focused**: Focused day button receives visible focus ring
 *
 * ### Accessibility
 * - Full keyboard navigation: arrow keys to move between days, month/year dropdowns are keyboard-accessible
 * - Weekday column headers use locale-aware labels for screen readers
 * - Selected day renders as a pressed toggle button
 * - Disabled dates use `aria-disabled` so they remain discoverable by screen readers
 *
 * ### Customization
 * Advanced: override internal rendering via the `components` and `formatters` props from `react-day-picker`. See the [react-day-picker documentation](https://react-day-picker.js.org/api/components) for details. Use at your own risk — overrides may break styling or accessibility.
 */
export const Calendar: React.FC<CalendarProps> = ({
  className,
  'data-spellbook-name': spellbookName,
  classNames,
  components,
  formatters,
  onSelect,
  selected,
  required = false,
  locale: providedLocale = 'en-US',
  showOutsideDays = false,
  ...props
}) => {
  const dayPickerClassNames = getDefaultClassNames();
  const locale = new Intl.Locale(providedLocale);

  return (
    <DayPicker
      mode="single"
      captionLayout="dropdown"
      showOutsideDays={showOutsideDays}
      navLayout="around"
      required={required}
      selected={selected}
      onSelect={onSelect}
      className={cn(
        'group/calendar',
        'p-1',
        String.raw`rtl:**:[.rdp-button\_next>svg]:scale-x-[-1]`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:scale-x-[-1]`,
        className,
      )}
      formatters={{
        formatMonthDropdown: (date) => date.toLocaleString(locale, { month: 'short' }),
        formatMonthCaption: (date: Date) => date.toLocaleString(locale, { month: 'long' }),
        formatYearCaption: (date: Date) => date.toLocaleString(locale, { year: 'numeric' }),
        formatWeekdayName: (weekday) => weekday.toLocaleString(locale, { weekday: 'narrow' }),
        ...formatters,
      }}
      classNames={{
        button_next: cn(
          dayPickerClassNames.button_next,
          buttonVariants({ color: 'neutral', variant: 'ghost', size: 'sm', icon: true, shape: 'default' }),
          'flex-0 min-w-9 max-w-9 min-h-9 absolute ltr:right-0 rtl:left-0 top-0',
        ),
        button_previous: cn(
          dayPickerClassNames.button_previous,
          buttonVariants({ color: 'neutral', variant: 'ghost', size: 'sm', icon: true, shape: 'default' }),
          'flex-0 min-w-9 max-w-9 min-h-9 absolute ltr:left-0 rtl:right-0 top-0',
        ),
        day: cn(dayPickerClassNames.day, ['relative', 'h-full', 'p-0', 'text-center', 'group/day', 'aspect-square', 'select-none', 'mb-1']),
        dropdown: cn(dayPickerClassNames.dropdown, 'flex-1 w-full text-2xs'),
        dropdowns: cn(dayPickerClassNames.dropdowns, [
          'flex-1',
          'flex',
          'items-center',
          'justify-center',
          'gap-1',
          'w-full',
          'max-w-[calc(100%_-_5rem)]',
          'absolute',
          'top-0',
        ]),
        hidden: cn(dayPickerClassNames.hidden, 'invisible'),
        month: cn(dayPickerClassNames.month, 'flex flex-col w-full gap-2 relative'),
        month_caption: cn(dayPickerClassNames.month_caption, ['flex', 'items-center', 'justify-center', 'w-full']),
        month_grid: cn(dayPickerClassNames.month_grid, 'mt-11'),
        months: cn(dayPickerClassNames.months, 'flex'),
        nav: cn(dayPickerClassNames.nav, 'flex items-center gap-1 w-full absolute top-0 inset-x-0 justify-between'),
        root: cn(dayPickerClassNames.root, 'w-fit'),
        table: 'w-full border-collapse',
        weekday: cn(
          dayPickerClassNames.weekday,
          textVariants({ variant: 'caption-2', color: 'default-soft' }),
          'flex-1 text-center select-none mb-2',
        ),
        weekdays: cn(dayPickerClassNames.weekdays, 'flex'),
        week: cn(dayPickerClassNames.week, 'flex w-full first-of-type:justify-end'),
        caption_label: dayPickerClassNames.caption_label,
        disabled: dayPickerClassNames.disabled,
        dropdown_root: dayPickerClassNames.dropdown_root,
        outside: dayPickerClassNames.outside,
        range_end: dayPickerClassNames.range_end,
        range_middle: dayPickerClassNames.range_middle,
        range_start: dayPickerClassNames.range_start,
        today: dayPickerClassNames.today,
        week_number: dayPickerClassNames.week_number,
        week_number_header: dayPickerClassNames.week_number_header,
        ...classNames,
      }}
      components={{
        Chevron: ({ className, orientation, size, disabled, ...props }) => {
          let iconName: React.ComponentProps<typeof Icon>['name'];

          switch (orientation) {
            case 'left':
              iconName = 'chevron-left';
              break;
            case 'right':
              iconName = 'chevron-right';
              break;
            default:
              iconName = 'chevron-down';
              break;
          }

          return <Icon size="sm" name={iconName} color={disabled ? 'disabled' : 'default'} className={className} {...props} />;
        },

        DayButton: CalendarDayButton,
        Dropdown: ({ className, options, onChange, size: _, ...props }) => {
          return (
            <Select
              onValueChange={(value) => {
                if (onChange) {
                  const event = { target: { value } } as React.ChangeEvent<HTMLSelectElement>;
                  onChange(event);
                }
              }}
              className={cn(
                '!min-w-0',
                '[&_[data-spellbook-name=select-label]]:!text-xs',
                '[&_[data-spellbook-name=select-label]]:!font-medium',
                '[&_[role=combobox]]:gap-0.5',
                '[&_[role=combobox]]:pe-1',
                '[&_span]:me-0',
                '[&_svg]:ms-0.5',
                'w-18',
                className,
              )}
              {...(props as React.ComponentProps<typeof Select>)}
            >
              {options?.map((option) => (
                <Select.Option key={option.value} value={option.value as unknown as string}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          );
        },
        Weekday: ({ className, children, 'aria-label': ariaLabel, ...props }) => {
          return (
            // explicitly setting the aria-label to avoid issues with different languages
            // where the weekday name aria-label is _always_ english in react-day-picker
            <td className={cn(className)} aria-label={typeof children === 'string' ? children : ariaLabel} {...props}>
              <span>{children}</span>
            </td>
          );
        },
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-spellbook-name="calendar"
              {...(spellbookName && { 'data-spellbook-name': spellbookName })}
              ref={rootRef}
              className={cn('flex flex-col w-full mt-3 px-2', className)}
              {...props}
            />
          );
        },
        ...components,
      }}
      {...props}
    />
  );
};
